// hooks
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useGeneratePdf } from "./useGeneratePdf";

// Global queue to serialize PDF generation — concurrent html2canvas calls
// corrupt shared DOM state (CSS fixes in head, dark class removal)
let pdfQueue: Promise<void> = Promise.resolve();

const enqueuePdfGeneration = (fn: () => Promise<void>): Promise<void> => {
  pdfQueue = pdfQueue.then(fn, fn);
  return pdfQueue;
};

interface GenericPdfContentProps {
  children: React.ReactNode;
  hasCharts?: boolean;
  onPdfGeneratedSuccess?: (pdfBlob: Blob) => void;
  onPdfGenerateLoading?: () => void;
  onPdfError?: (error: Error) => void;
  onProgress?: (percent: number, label: string) => void;
}

export const GenericPdfContent = forwardRef<
  {
    generatePdf: () => Promise<Blob | void>;
  },
  GenericPdfContentProps
>(
  (
    { children, hasCharts = false, onPdfGeneratedSuccess, onPdfGenerateLoading, onPdfError, onProgress },
    ref
  ) => {
    const pdfId = useId();
    const { generatePdf } = useGeneratePdf();
    const hasGeneratedRef = useRef(false);
    const callbacksRef = useRef({
      onPdfGenerateLoading,
      onPdfGeneratedSuccess,
      onPdfError,
      onProgress,
    });

    // Update callbacks ref when they change
    useEffect(() => {
      callbacksRef.current = {
        onPdfGenerateLoading,
        onPdfGeneratedSuccess,
        onPdfError,
        onProgress,
      };
    }, [onPdfGenerateLoading, onPdfGeneratedSuccess, onPdfError, onProgress]);

    const handleGeneratePdf = useCallback(async (): Promise<Blob | void> => {
      // Prevent multiple PDF generations
      if (hasGeneratedRef.current) {
        return;
      }
      hasGeneratedRef.current = true;

      if (callbacksRef.current.onPdfGenerateLoading) {
        callbacksRef.current.onPdfGenerateLoading();
      }

      callbacksRef.current.onProgress?.(55, "Przygotowywanie dokumentu...");

      // Enqueue to prevent concurrent html2canvas calls corrupting shared DOM
      return enqueuePdfGeneration(async () => {
        try {
          const pdfContentElement = document.querySelector(
            `[data-pdf-id="${CSS.escape(pdfId)}"]`
          ) as HTMLElement;

          if (pdfContentElement) {
            const pdfBlob = await generatePdf(pdfContentElement, (percent, label) => {
              callbacksRef.current.onProgress?.(percent, label);
            });
            callbacksRef.current.onProgress?.(95, "Pobieranie...");
            if (callbacksRef.current.onPdfGeneratedSuccess) {
              callbacksRef.current.onPdfGeneratedSuccess(pdfBlob);
            }
          } else {
            console.error("PDF content element not found!");
            hasGeneratedRef.current = false;
          }
        } catch (error) {
          console.error("Error during generating PDF:", error);
          hasGeneratedRef.current = false;
          if (callbacksRef.current.onPdfError) {
            callbacksRef.current.onPdfError(error as Error);
          }
        }
      });
    }, [generatePdf, pdfId]);

    useEffect(() => {
      if (hasGeneratedRef.current) {
        return;
      }

      if (!hasCharts) {
        // No charts — short delay for layout to settle
        callbacksRef.current.onProgress?.(10, "Ładowanie danych...");
        const timer = setTimeout(() => {
          callbacksRef.current.onProgress?.(50, "Przygotowywanie dokumentu...");
          handleGeneratePdf();
        }, 1000);
        return () => clearTimeout(timer);
      }

      // With charts — poll until all PdfChart components have converted to images
      let cancelled = false;
      callbacksRef.current.onProgress?.(10, "Ładowanie danych...");
      const poll = async () => {
        const start = Date.now();

        while (!cancelled && Date.now() - start < 20000) {
          const el = document.querySelector(`[data-pdf-id="${CSS.escape(pdfId)}"]`);

          // Check PdfChart components (data-pdf-chart attribute)
          const pdfCharts = el?.querySelectorAll("[data-pdf-chart]") ?? [];
          if (pdfCharts.length > 0) {
            const pending = el?.querySelectorAll('[data-pdf-chart="pending"]') ?? [];
            const total = pdfCharts.length;
            const ready = total - pending.length;
            const chartProgress = Math.round(15 + (ready / total) * 35);
            callbacksRef.current.onProgress?.(chartProgress, `Renderowanie wykresów... (${ready}/${total})`);
            if (pending.length === 0) {
              // All PdfChart components have converted to images
              break;
            }
          } else {
            // Fallback: check for recharts SVGs directly (non-PdfChart mode)
            const containers = el?.querySelectorAll('[pdf-chart-container="true"]') ?? [];
            const svgs = el?.querySelectorAll(".recharts-surface") ?? [];
            callbacksRef.current.onProgress?.(15, "Renderowanie wykresów...");
            if (containers.length > 0 && svgs.length >= containers.length) {
              break;
            }
          }

          await new Promise((r) => setTimeout(r, 200));
        }
        if (!cancelled) {
          callbacksRef.current.onProgress?.(50, "Renderowanie wykresów zakończone");
          handleGeneratePdf();
        }
      };
      poll();
      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        generatePdf: handleGeneratePdf,
      }),
      [handleGeneratePdf]
    );

    return <div data-pdf-id={pdfId}>{children}</div>;
  }
);

GenericPdfContent.displayName = "GenericPdfContent";
